import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/layout/Header';
import { uploadPaymentProof, getDigitalOrderStatus } from '../services/digitalProductApi';
import Tesseract from 'tesseract.js';
import { formatCurrency } from '../utils/formatters';
import DynaQRISModal from '../components/common/DynaQRISModal';
import { getPublicPaymentConfig, generateDynaQRIS, verifyDynaQRISPayment } from '../services/paymentApi';
import '../styles/Body.css';

const DigitalProductPaymentPage = () => {
    const { orderNumber } = useParams();
    const navigate = useNavigate();
    const [proofFile, setProofFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [order, setOrder] = useState(null);
    const [success, setSuccess] = useState(false);
    const [ocrError, setOcrError] = useState('');
    const [ocrLoading, setOcrLoading] = useState(false);

    // DynaQRIS State
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [showDynaModal, setShowDynaModal] = useState(false);
    const [qrisData, setQrisData] = useState(null);
    const [generatingQris, setGeneratingQris] = useState(false);

    const getMediaUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${process.env.REACT_APP_API_BASE_URL || ''}${url}`;
    };

    useEffect(() => {
        const fetchOrderAndConfig = async () => {
            setLoading(true);
            setFetchError(false);
            try {
                const [orderRes, configRes] = await Promise.all([
                    getDigitalOrderStatus(orderNumber),
                    getPublicPaymentConfig().catch(() => null)
                ]);
                if (orderRes?.data) {
                    setOrder(orderRes.data);
                    setPaymentConfig(configRes);
                    if (configRes?.active_mode === 'dynaqris' && orderRes.data?.amount) {
                        handleGenerateDynaQRIS(orderRes.data.amount);
                    }
                } else {
                    setFetchError(true);
                }
            } catch (err) {
                console.error('Error fetching order and config:', err);
                setFetchError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchOrderAndConfig();
    }, [orderNumber]);

    const handleGenerateDynaQRIS = async (amtOverride) => {
        const targetAmt = amtOverride || order?.amount;
        if (!targetAmt) return;
        setGeneratingQris(true);
        try {
            const res = await generateDynaQRIS({
                amount: targetAmt,
                reference_id: orderNumber,
                type: 'digital',
                add_unique_code: false
            });
            if (res.error) {
                alert(res.error);
            } else {
                setQrisData(res);
                setShowDynaModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Gagal menghasilkan QRIS Dinamis.');
        } finally {
            setGeneratingQris(false);
        }
    };

    const handleDynaSuccess = async (res) => {
        setShowDynaModal(false);
        try {
            await verifyDynaQRISPayment('digital', orderNumber);
            setSuccess(true);
        } catch (err) {
            console.error(err);
            setSuccess(true);
        }
    };


    const sendWhatsAppMessage = async (phone, message) => {
        try {
            // Usually this would call a backend endpoint that sends WA
            // For now we'll just log it or call a hypothetical service
            console.log(`Sending WA to ${phone}: ${message}`);
            // await axios.post('/api/wa/send', { phone, message });
        } catch (err) {
            console.error('Failed to send WA:', err);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Ukuran file terlalu besar. Maksimal 5MB.');
                return;
            }
            if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
                alert('Format file tidak didukung. Hanya JPG, PNG, JPEG, dan WebP yang diperbolehkan.');
                return;
            }
            setProofFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const preprocessImageForOCR = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        const MAX_WIDTH = 1200;
                        const MAX_HEIGHT = 1200;
                        let width = img.width;
                        let height = img.height;

                        if (width > height) {
                            if (width > MAX_WIDTH) {
                                height = Math.round((height * MAX_WIDTH) / width);
                                width = MAX_WIDTH;
                            }
                        } else {
                            if (height > MAX_HEIGHT) {
                                width = Math.round((width * MAX_HEIGHT) / height);
                                height = MAX_HEIGHT;
                            }
                        }

                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);

                        const imgData = ctx.getImageData(0, 0, width, height);
                        const d = imgData.data;
                        for (let i = 0; i < d.length; i += 4) {
                            const avg = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114);
                            d[i] = avg;
                            d[i + 1] = avg;
                            d[i + 2] = avg;
                        }
                        ctx.putImageData(imgData, 0, 0);

                        canvas.toBlob((blob) => {
                            resolve(blob || file);
                        }, 'image/jpeg', 0.85);
                    } catch (err) {
                        resolve(file);
                    }
                };
                img.onerror = () => resolve(file);
                img.src = e.target.result;
            };
            reader.onerror = () => resolve(file);
            reader.readAsDataURL(file);
        });
    };

    const handleProofSubmit = async (e) => {
        e.preventDefault();
        if (!proofFile) {
            alert('Silakan pilih file bukti pembayaran');
            return;
        }

        if (!order) {
            alert('Data pesanan belum dimuat');
            return;
        }

        setUploading(true);
        setOcrError('');
        setOcrLoading(true);

        let ocrFailedReason = '';

        try {
            // -- OCR VALIDATION BLOCK WITH RESILIENT PREPROCESSING --
            console.log("Mulai proses OCR mobile-friendly...");
            const optimizedBlob = await preprocessImageForOCR(proofFile);

            try {
                const { data: { text } } = await Tesseract.recognize(optimizedBlob, 'ind');
                const lowerText = (text || '').toLowerCase();
                console.log("Hasil OCR:", text);

                const numericTotal = Math.floor(Number(order.amount));
                const totalStr = String(numericTotal);
                const totalFormatted = new Intl.NumberFormat('id-ID').format(numericTotal);

                const scrubbedOCR = lowerText.replace(/rp/g, '').replace(/\./g, '').replace(/,/g, '').replace(/\s+/g, '');

                const isAmountPresent =
                    (text && text.includes(totalStr)) ||
                    (text && text.includes(totalFormatted)) ||
                    scrubbedOCR.includes(totalStr);

                let isRecipientValid = false;
                let expectedRecipientName = 'BAE Community / Barakah Economy';
                if (order.paid_to_seller_directly && order.seller_bank_holder) {
                    expectedRecipientName = order.seller_bank_holder;
                    isRecipientValid = lowerText.includes(order.seller_bank_holder.toLowerCase());
                } else {
                    isRecipientValid = lowerText.includes('bae community') || lowerText.includes('barakah economy') || lowerText.includes('gopay') || lowerText.includes('barakah');
                }

                if (!isRecipientValid) {
                    ocrFailedReason = `Validasi Gagal: Struk tidak mencantumkan nama "${expectedRecipientName}". Pastikan Anda transfer ke rekening yang benar.`;
                } else if (!isAmountPresent) {
                    ocrFailedReason = `Validasi Gagal: Nominal struk tidak sesuai dengan total tagihan (Rp ${totalFormatted}). Pastikan nominal transfer pas.`;
                }
            } catch (tesseractErr) {
                console.warn("Mobile Tesseract WebAssembly constraint, gracefully allowing submission with proof file:", tesseractErr);
            }

            if (ocrFailedReason) {
                setOcrError(ocrFailedReason);
                setUploading(false);
                setOcrLoading(false);
                return;
            }
            // -- END OCR VALIDATION --

            const formData = new FormData();
            formData.append('payment_proof', proofFile);
            await uploadPaymentProof(orderNumber, formData);

            // Send WA Notification
            try {
                const waMsg = `Halo kak ${order.buyer_name},\n\nTerima kasih telah melakukan pembayaran untuk pesanan digital (Order: *${order.order_number}*).\n\nPesanan kakak telah terverifikasi dan link produk akan segera dikirim ke email ${order.buyer_email}.`;
                await sendWhatsAppMessage(order.buyer_phone, waMsg);
            } catch (waErr) {
                console.error("Gagal mengirim WA:", waErr);
            }

            setSuccess(true);
        } catch (err) {
            console.error(err);
            setOcrError('Gagal mengupload bukti pembayaran. Silakan coba lagi.');
        } finally {
            setUploading(false);
            setOcrLoading(false);
        }
    };

    if (success) {
        return (
            <div className="body">
                <Header />
                <div className="px-4 py-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="material-icons text-green-600 text-4xl">check_circle</span>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Pembayaran Berhasil!</h1>
                    <p className="text-gray-500 text-sm mb-6">
                        Bukti pembayaran telah diverifikasi. Link produk digital telah dikirim ke email Anda.
                    </p>
                    <p className="text-xs text-gray-400 mb-6">Order: {orderNumber}</p>
                    <button
                        onClick={() => navigate('/digital-products')}
                        className="px-8 py-3 bg-green-700 text-white rounded-xl font-bold hover:bg-green-800 transition"
                    >
                        Kembali ke Produk Digital
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="body min-h-screen bg-slate-50">
                <Header />
                <div className="flex flex-col items-center justify-center py-28 px-4 text-center">
                    <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4" />
                    <h2 className="text-base font-bold text-slate-800">Menyiapkan Informasi Pembayaran...</h2>
                    <p className="text-xs text-slate-500 mt-1">Sedang memuat data tagihan pesanan #{orderNumber}.</p>
                </div>
            </div>
        );
    }

    if (fetchError || !order) {
        return (
            <div className="body min-h-screen bg-slate-50">
                <Header />
                <div className="max-w-md mx-auto py-24 px-4 text-center">
                    <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="material-icons text-3xl">error_outline</span>
                    </div>
                    <h2 className="text-lg font-bold text-slate-800">Pesanan Tidak Ditemukan</h2>
                    <p className="text-xs text-slate-500 mt-1 mb-6">
                        Data pesanan #{orderNumber} tidak ditemukan atau belum tercatat di sistem.
                    </p>
                    <button
                        onClick={() => navigate('/digital-products')}
                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-md"
                    >
                        Kembali ke Produk Digital
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="body">
            <Helmet>
                <title>Pembayaran - {orderNumber}</title>
            </Helmet>

            <Header />

            <div className="px-4 py-4 pb-32">
                <h1 className="text-lg font-bold mb-2">Pembayaran Produk Digital</h1>
                <p className="text-sm text-gray-500 mb-6">Order: <strong>{orderNumber}</strong></p>

                {/* QRIS Banner if mode is dynaqris */}
                {paymentConfig?.active_mode === 'dynaqris' && (
                    <div className="bg-gradient-to-r from-emerald-600 to-green-700 text-white rounded-2xl p-5 mb-6 shadow-lg text-center">
                        <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                            <span className="material-icons text-sm">qr_code_2</span>
                            <span>Metode Pembayaran QRIS Otomatis</span>
                        </div>
                        <h3 className="text-lg font-bold">Scan QRIS Pembayaran</h3>
                        <p className="text-xs text-emerald-100 mt-1 mb-4">
                            Pembayaran otomatis terverifikasi dan langsung memberikan akses produk digital.
                        </p>
                        <button
                            onClick={() => handleGenerateDynaQRIS()}
                            disabled={generatingQris}
                            className="w-full bg-white text-emerald-800 font-bold py-3 px-4 rounded-xl shadow hover:bg-emerald-50 transition flex items-center justify-center gap-2 text-sm"
                        >
                            <span className="material-icons text-base">qr_code_scanner</span>
                            <span>{generatingQris ? 'Membuat QRIS...' : 'Tampilkan QRIS Pembayaran'}</span>
                        </button>
                    </div>
                )}

                <DynaQRISModal
                    isOpen={showDynaModal}
                    onClose={() => setShowDynaModal(false)}
                    qrisData={qrisData}
                    transactionType="digital"
                    referenceId={orderNumber}
                    amount={order?.amount}
                    onPaymentSuccess={handleDynaSuccess}
                />

                {order && (
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-4 flex justify-between items-center shadow-xs">
                        <div>
                            <p className="text-[10px] text-green-600 uppercase font-bold tracking-wider">Total Tagihan</p>
                            <p className="text-xl font-black text-green-900">
                                Rp {formatCurrency(order.amount)}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Status</p>
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-orange-100 text-orange-700">
                                {order.payment_status}
                            </span>
                        </div>
                    </div>
                )}

                {/* Manual Transfer & Proof Upload only shown if mode is NOT dynaqris */}
                {paymentConfig?.active_mode !== 'dynaqris' && (
                    <>
                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                            <h2 className="font-semibold text-sm mb-3">Pilihan Pembayaran Manual</h2>

                            {/* QRIS Manual */}
                            {order && (
                                <div className="bg-white rounded-lg p-4 text-center border border-gray-200 mb-4">
                                    <p className="text-xs font-bold text-gray-400 mb-2 uppercase tracking-wider">
                                        {order.paid_to_seller_directly ? `QRIS ${order.seller_bank_holder}` : "QRIS BAE COMMUNITY"}
                                    </p>
                                    <img
                                        id="qris-image"
                                        src={order.paid_to_seller_directly && order.seller_qris_image ? getMediaUrl(order.seller_qris_image) : "/images/qris-bae2.png"}
                                        alt="QRIS Code"
                                        className="w-48 h-48 mx-auto object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="hidden w-48 h-48 mx-auto bg-gray-100 rounded-lg items-center justify-center text-gray-400 text-sm">
                                        QRIS Code
                                    </div>

                                    <button
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = order.paid_to_seller_directly && order.seller_qris_image ? getMediaUrl(order.seller_qris_image) : '/images/qris-bae2.png';
                                            link.download = `QRIS-${orderNumber}.png`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                        className="mt-4 flex items-center justify-center gap-2 mx-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-200 transition border border-gray-200"
                                    >
                                        <span className="material-icons text-sm">download</span>
                                        UNDUH QR
                                    </button>
                                </div>
                            )}

                            <div className="border-t border-gray-200 my-4"></div>

                            {/* BSI */}
                            {order && (
                                <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        {!order.paid_to_seller_directly && <img src="/images/bsi-logo.png" alt="BSI" className="h-4" onError={(e) => e.target.style.display = 'none'} />}
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            TRANSFER BANK {order.paid_to_seller_directly ? order.seller_bank_name : "BSI"}
                                        </p>
                                    </div>
                                    <div className="flex justify-between items-center bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                                        <div>
                                            <p className="text-xs text-emerald-600">No. Rekening {order.paid_to_seller_directly ? order.seller_bank_name : "BSI"}</p>
                                            <p className="font-bold text-emerald-900">{order.paid_to_seller_directly ? order.seller_bank_account : "2220606662"}</p>
                                            <p className="text-xs text-emerald-700">an. {order.paid_to_seller_directly ? order.seller_bank_holder : "Barakah Economy Community"}</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(order.paid_to_seller_directly ? order.seller_bank_account : '2220606662');
                                                alert('No. Rekening berhasil disalin!');
                                            }}
                                            className="bg-emerald-600 text-white text-[10px] px-3 py-1 rounded-full font-bold shadow-sm"
                                        >
                                            SALIN
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {ocrError && (
                            <div className="bg-red-50 text-red-600 text-xs p-3 rounded-xl mb-4 border border-red-100">
                                <p className="font-bold">Error Validasi:</p>
                                <p>{ocrError}</p>
                            </div>
                        )}

                        {/* Upload */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Bukti Transfer</label>
                            <div
                                onClick={() => document.getElementById('proof-input').click()}
                                className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-green-400 transition"
                            >
                                {preview ? (
                                    <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                                ) : (
                                    <>
                                        <span className="material-icons text-gray-400 text-4xl mb-2">cloud_upload</span>
                                        <p className="text-sm text-gray-500">Klik untuk memilih gambar</p>
                                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP maksimal 5MB</p>
                                    </>
                                )}
                            </div>
                            <input
                                id="proof-input"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>

                        <button
                            onClick={handleProofSubmit}
                            disabled={uploading || ocrLoading || !proofFile}
                            className="w-full bg-green-700 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg hover:bg-green-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {(uploading || ocrLoading) ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>{ocrLoading ? 'Validasi OCR...' : 'Memproses...'}</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-icons text-base">upload</span>
                                    <span>Kirim Bukti Pembayaran</span>
                                </>
                            )}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default DigitalProductPaymentPage;
